import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { Subject, takeUntil, forkJoin } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DrawerService } from 'src/app/core/services/drawer.service';
import { SnackbarService } from 'src/app/shared/snackbar/snackbar.service';
import { DRAWER_DATA } from 'src/app/app.component';
import type { OrderDisplayModel } from 'src/app/pages/orders/models/order-display.model';
import {
  StockAssignmentService, AvailableAgendaDto,
  AddStockToStoreDto,
  ReserveStockForAgendaDto,
  UpdateOrderProductStatusDTO
} from 'src/app/services/stock-assignment.service';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { OrderDetail, OrderItem } from 'src/app/pages/orders/services/orders.service';
import { AnalyticsService } from 'src/app/analytics/analytics.service';
import { AnalyticsEvent } from 'src/app/analytics/models/analytics-event';

export interface AddStockDrawerData {
  orderData: OrderDisplayModel;
  orderProduct: OrderDetail;
  // warehouseName?: string;
  // productId?: number;
  // storeId?: number;
  // quantity?: number;
  // cost?: number;
}

export interface ProductData {
  id: number;
  code: string;
  brand: string;
  model: string;
  name: string;
  quantity: number;
  cost?: number;
}

export interface WarehouseData {
  id: number;
  name: string;
  currentStock: number;
  newStock: number;
  cost?: number;
}

enum AssignmentModeOption {
  WAREHOUSE = "WAREHOUSE",
  AGENDA = "AGENDA",

}

export interface AddStockForm {
  mode: FormControl<AssignmentModeOption>;
  dot: FormControl<string>
  locations: FormControl<string[]>;
  cost: FormControl<number>;
  agendaId: FormControl<number | null>;
  locationText: FormControl<string>

  productId: FormControl<number>
  storeId: FormControl<number>
  orderId: FormControl<number>
  stockQuantity: FormControl<number>
}


@Component({
  selector: 'app-add-stock-drawer',
  templateUrl: './add-stock-drawer.component.html',
  styleUrls: ['./add-stock-drawer.component.scss']
})
export class AddStockDrawerComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  form: FormGroup<AddStockForm> | null = null;

  isLoading = false;
  ProductData: ProductData | null = null;
  WarehouseData: WarehouseData | null = null;
  AvailableAgendas: AvailableAgendaDto[] = [];
  SelectedAgendas: AvailableAgendaDto[] = [];

  AssignmentModeOption = AssignmentModeOption
  AssignmentModeOptions = Array.from(Object.values(AssignmentModeOption))

  LocationsSelected: string[] = [];
  LocationText: string = "";
  dot: string = "";

  constructor(
    private readonly drawerService: DrawerService,
    private readonly translate: TranslateService,
    private readonly snackbarService: SnackbarService,
    private readonly stockAssignmentService: StockAssignmentService,
    private readonly fb: FormBuilder,
    private readonly analytics: AnalyticsService,
    @Inject(DRAWER_DATA) public data: AddStockDrawerData
  ) { }

  ngOnInit(): void {
    this.buildForm(this.data)
    this.loadProductDataFromOrder();
    this.loadAvailableAgendas();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildForm(data: AddStockDrawerData): void {

    const productId = data.orderProduct.productId ? data.orderProduct.productId : -1;
    const storeId = data.orderData.storeId ? data.orderData.storeId : -1;
    const stockQuantiy = data.orderProduct.quantity ? data.orderProduct.quantity : -1;

    this.form = this.fb.nonNullable.group<AddStockForm>({
      productId: this.fb.nonNullable.control<number>(productId, [Validators.required, Validators.min(0)]),
      storeId: this.fb.nonNullable.control<number>(storeId, [Validators.required, Validators.min(0)]),
      orderId: this.fb.nonNullable.control<number>(Number(data.orderData.id), [Validators.required]),
      stockQuantity: this.fb.nonNullable.control<number>(Number(stockQuantiy), [Validators.required, Validators.min(1)]),

      mode: this.fb.nonNullable.control<AssignmentModeOption>(AssignmentModeOption.WAREHOUSE),
      dot: this.fb.nonNullable.control<string>("", [Validators.required]),
      locations: this.fb.nonNullable.control<string[]>([]),
      cost: this.fb.nonNullable.control<number>(data.orderProduct.price ?? 0, [Validators.required, Validators.min(0.01)]),
      agendaId: this.fb.control<number | null>(null),
      locationText: this.fb.nonNullable.control<string>("")

    });

    this.setupModeLogic()
  }

  addLocation() {
    const locationValue = this.form?.controls.locationText.value?.trim();
    const current = this.form?.controls.locations.value ?? [];
    if (locationValue && !current.includes(locationValue)) {
      this.updateLocations([...current, locationValue]);
      this.form?.controls.locationText.setValue("");
    }
  }

  removeLocations(index: number): void {
    const locations = this.form?.controls.locations.value ? [...this.form.controls.locations.value] : []
    locations.splice(index, 1);
    this.updateLocations(locations);
  }

  private updateLocations(locations: string[]): void {
    this.form?.controls.locations.setValue(locations);
  }

  private loadProductDataFromOrder(): void {
    if (this.data?.orderData) {
      const order = this.data.orderData;
      const orderDetails = order.orderDetails;
      if (orderDetails && orderDetails.length > 0) {
        const firstProduct = orderDetails[0];
        const productName = firstProduct.productName || '';
        this.ProductData = {
          id: this.data.orderProduct.productId || firstProduct.productId || 0,
          code: firstProduct.productId?.toString() || '',
          brand: this.extractBrand(productName),
          model: this.extractModel(productName),
          name: productName,
          quantity: this.data.orderProduct.quantity || firstProduct.quantity || 0,
          cost: this.data.orderProduct.price
        };
        this.WarehouseData = {
          id: this.data.orderData.storeId || order.storeId || 0,
          name: this.data.orderData.storeName || order.storeName || 'Almacén desconocido',
          currentStock: 0,
          newStock: 0 + (this.data.orderProduct.quantity || firstProduct.quantity || 0),
        };
      }
    }
  }

  private loadAvailableAgendas(): void {
    if (!this.ProductData?.id || !this.WarehouseData?.id) {
      return;
    }
    const fromDate = new Date();
    const toDate = new Date();
    toDate.setDate(toDate.getDate() + 30);
    const params = {
      productId: this.ProductData.id,
      storeId: this.WarehouseData.id,
      fromDate: fromDate.toISOString(),
      toDate: toDate.toISOString(),
      status: 'PENDING'
    };
    this.stockAssignmentService.getAvailableAgendas(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (agendas: AvailableAgendaDto[]) => {
          this.AvailableAgendas = (agendas ?? []).filter((agenda) => agenda.isEligibleForReservation);
        },
        error: () => {
          this.AvailableAgendas = [];
        }
      });
  }

  private extractBrand(productName: string): string {
    if (!productName) return '';
    const words = productName.split(' ');
    return words[0] || '';
  }

  private extractModel(productName: string): string {
    if (!productName) return '';
    const words = productName.split(' ');
    return words.slice(1).join(' ') || '';
  }

  onCancel(): void {
    this.drawerService.closeDrawer();
  }

  onSubmit(): void {
    if (this.isLoading || !this.ProductData || !this.WarehouseData) {
      return;
    }
    if (!this.WarehouseData.id || this.WarehouseData.id <= 0) {
      this.snackbarService.showError(
        'Invalid store found in order data. The order must include a valid storeId.'
      );
      return;
    }
    this.isLoading = true;
    this.addStockToStore()
  }

  private addStockToStore(): void {
    if (!this.ProductData || !this.WarehouseData) {
      return;
    }
    if (!this.WarehouseData.id || this.WarehouseData.id <= 0) {
      this.isLoading = false;
      this.snackbarService.showError(
        'Invalid store found in order data. The order must include a valid storeId.'
      );
      return;
    }

    const formValue = this.form?.getRawValue()

    const request: UpdateOrderProductStatusDTO = {
      productId: formValue?.productId!,
      storeId: formValue?.storeId!,
      stockQuantity: formValue?.stockQuantity!,
      locations: formValue?.locations.length ? formValue?.locations : undefined,
      dot: formValue?.dot!,
      price: formValue?.cost!,
      appointmentId: formValue?.agendaId
    };

    if (request.appointmentId) {
      this.analytics.track(AnalyticsEvent.OrderReserveForAgendaClick, {
        ...this.analytics.globalProps(),
        order_id: formValue?.orderId!,
        appointment_id: request.appointmentId,
        items_count: request.stockQuantity
      });

    } else {
      this.analytics.track(AnalyticsEvent.OrderAddToWarehouseClick, {
        ...this.analytics.globalProps(),
        order_id: formValue?.orderId!,
        warehouse_id: request.storeId,
        items_count: request.stockQuantity
      });
    }


    this.stockAssignmentService.updateOrderProductStatus(formValue?.orderId!, request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          // if (response.success) {
          this.snackbarService.showSuccess(
            this.translate.instant('pages.stock.add_stock.success_message')
          );
          this.drawerService.closeDrawer(true);
          // } else {
          //   this.snackbarService.showError(
          //     response.message || this.translate.instant('pages.stock.add_stock.error_message')
          //   );
          // }
        },
        error: (error) => {
          this.isLoading = false;
          this.snackbarService.showError(
            this.translate.instant('pages.stock.add_stock.error_message')
          );
        }
      });
  }

  // private reserveStockForAgenda(): void {
  //   if (!this.ProductData || !this.WarehouseData || this.SelectedAgendas.length === 0) {
  //     this.snackbarService.showWarning(
  //       this.translate.instant('pages.stock.add_stock.no_agendas_selected')
  //     );
  //     this.isLoading = false;
  //     return;
  //   }
  //   const request: ReserveStockForAgendaDto = {
  //     orderId: Number(this.data.orderData?.id) || 0,
  //     productId: this.ProductData.id,
  //     storeId: this.WarehouseData.id,
  //     quantity: this.ProductData.quantity,
  //     agendaIds: this.SelectedAgendas.map(a => a.id),
  //     notes: `Stock reserved for ${this.SelectedAgendas.length} agenda(s)`,
  //     cost: this.ProductData.cost ?? 0
  //   };
  //   this.stockAssignmentService.reserveStockForAgenda(request)
  //     .pipe(takeUntil(this.destroy$))
  //     .subscribe({
  //       next: (response: any) => {
  //         this.isLoading = false;
  //         if (response.success) {
  //           this.snackbarService.showSuccess(
  //             response.message || this.translate.instant('pages.stock.add_stock.reservation_success')
  //           );
  //           this.drawerService.closeDrawer(true);
  //         } else {
  //           this.snackbarService.showError(
  //             response.message || this.translate.instant('pages.stock.add_stock.reservation_error')
  //           );
  //         }
  //       },
  //       error: (error) => {
  //         this.isLoading = false;
  //         this.snackbarService.showError(
  //           this.translate.instant('pages.stock.add_stock.reservation_error')
  //         );
  //       }
  //     });
  // }

  toggleAgendaSelection(agenda: AvailableAgendaDto): void {
    const value = this.form?.controls.agendaId.value
    if (value === agenda.id) {
      this.form?.controls.agendaId.setValue(null)
    } else {
      this.form?.controls.agendaId.setValue(agenda.id)
    }
  }

  get currentMode(): AssignmentModeOption | null {
    if (!this.form) return null;
    return this.form?.controls.mode.value;
  }

  setAssignmentMode(mode: AssignmentModeOption): void {
    this.form?.controls.mode.setValue(mode);
  }

  private setupModeLogic(): void {
    const modeCtrl = this.form?.controls.mode;
    const agendaCtrl = this.form?.controls.agendaId;

    modeCtrl?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(mode => {
        if (mode === AssignmentModeOption.WAREHOUSE) {
          agendaCtrl?.reset(null);
          agendaCtrl?.clearValidators();
          this.SelectedAgendas = [];
        } else {
          agendaCtrl?.setValidators([Validators.required]);
        }

        agendaCtrl?.updateValueAndValidity({ emitEvent: false });
      });
  }

} 
